import React, { useState } from "react";
import styled from "styled-components";
import { useScrollLock } from "../../hooks/useScrollLock";
import { FiX, FiKey, FiUser, FiLock } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { FaEyeSlash, FaEye } from "react-icons/fa";

const DashboardAccessModals = ({
  activeModal,
  closeModal,
  selectedBand,
  onCreateAccess,
  onResetPassword,
  loading,
  adminUsername,
}) => {
  const [createAccessData, setCreateAccessData] = useState({
    username: "",
    password: "",
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  // Password visibility states
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useScrollLock(activeModal === "createAccess" || activeModal === "resetPassword");

  const validateCreateAccess = () => {
    const newErrors = {};

    if (!createAccessData.username.trim()) {
      newErrors.username = "Username je obavezan";
    } else if (createAccessData.username.length < 3) {
      newErrors.username = "Username mora imati najmanje 3 karaktera";
    }

    if (!createAccessData.password.trim()) {
      newErrors.password = "Password je obavezan";
    } else if (createAccessData.password.length < 6) {
      newErrors.password = "Password mora imati najmanje 6 karaktera";
    }

    return newErrors;
  };

  const validateResetPassword = () => {
    const newErrors = {};

    if (!resetPasswordData.newPassword.trim()) {
      newErrors.newPassword = "Nova šifra je obavezna";
    } else if (resetPasswordData.newPassword.length < 6) {
      newErrors.newPassword = "Šifra mora imati najmanje 6 karaktera";
    }

    if (!resetPasswordData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Potvrda šifre je obavezna";
    } else if (
      resetPasswordData.newPassword !== resetPasswordData.confirmPassword
    ) {
      newErrors.confirmPassword = "Šifre se ne poklapaju";
    }

    return newErrors;
  };

  // Check if passwords match in real time
  const passwordsMatch =
    resetPasswordData.newPassword &&
    resetPasswordData.confirmPassword &&
    resetPasswordData.newPassword === resetPasswordData.confirmPassword;

  const passwordsDontMatch =
    resetPasswordData.newPassword &&
    resetPasswordData.confirmPassword &&
    resetPasswordData.newPassword !== resetPasswordData.confirmPassword;

  const handleCreateAccessSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateCreateAccess();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onCreateAccess(selectedBand.id, createAccessData);
      setCreateAccessData({ username: "", password: "" });
    } catch (error) {
      setErrors({ general: error.message || "Greška pri kreiranju pristupa" });
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateResetPassword();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onResetPassword(selectedBand.id, resetPasswordData.newPassword);
      setResetPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      setErrors({ general: error.message || "Greška pri resetovanju šifre" });
    }
  };

  const handleCloseModal = () => {
    setErrors({});
    setCreateAccessData({ username: "", password: "" });
    setResetPasswordData({ newPassword: "", confirmPassword: "" });
    setShowCreatePassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    closeModal();
  };

  return (
    <AnimatePresence>
      {/* Modal za kreiranje pristupa */}
      {activeModal === "createAccess" && selectedBand && (
        <ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <ModalContent
            as={motion.div}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiKey style={{ marginRight: "0.5rem" }} />
                Kreiraj Dashboard Pristup
              </ModalTitle>
              <ModalClose onClick={handleCloseModal} disabled={loading}>
                <FiX />
              </ModalClose>
            </ModalHeader>

            <BandInfo>
              <p>
                <strong>Bend:</strong> {selectedBand.name}
              </p>
              <p>
                <strong>Žanr:</strong> {selectedBand.genre}
              </p>
            </BandInfo>

            {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

            <form onSubmit={handleCreateAccessSubmit}>
              <FormGroup>
                <FormLabel>
                  <FiUser style={{ marginRight: "0.5rem" }} />
                  Username *
                </FormLabel>
                <FormInput
                  type="text"
                  value={createAccessData.username}
                  onChange={(e) =>
                    setCreateAccessData((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  placeholder="Unesite username"
                  className={errors.username ? "error" : ""}
                  disabled={loading}
                />
                {errors.username && <ErrorText>{errors.username}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <FormLabel>
                  <FiLock style={{ marginRight: "0.5rem" }} />
                  Password *
                </FormLabel>
                <PasswordInputWrapper>
                  <FormInput
                    type={showCreatePassword ? "text" : "password"}
                    value={createAccessData.password}
                    onChange={(e) =>
                      setCreateAccessData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Unesite password"
                    className={errors.password ? "error" : ""}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowCreatePassword(!showCreatePassword)}
                    disabled={loading}
                  >
                    {showCreatePassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordInputWrapper>
                {errors.password && <ErrorText>{errors.password}</ErrorText>}
              </FormGroup>

              <ButtonGroup>
                <Button type="submit" $variant="primary" disabled={loading}>
                  {loading ? "Kreiram..." : "Kreiraj Pristup"}
                </Button>
                <Button
                  type="button"
                  $variant="secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  Otkaži
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal za reset password */}
      {activeModal === "resetPassword" && selectedBand && (
        <ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <ModalContent
            as={motion.div}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiLock style={{ marginRight: "0.5rem" }} />
                Reset Password
              </ModalTitle>
              <ModalClose onClick={handleCloseModal} disabled={loading}>
                <FiX />
              </ModalClose>
            </ModalHeader>

            <BandInfo>
              <p>
                <strong>Bend:</strong> {selectedBand.name}
              </p>
              <p>
                <strong>Trenutni username:</strong> {adminUsername || "N/A"}
              </p>
            </BandInfo>

            {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

            <form onSubmit={handleResetPasswordSubmit}>
              <FormGroup>
                <FormLabel>
                  <FiLock style={{ marginRight: "0.5rem" }} />
                  Nova Šifra *
                </FormLabel>
                <PasswordInputWrapper>
                  <FormInput
                    type={showNewPassword ? "text" : "password"}
                    value={resetPasswordData.newPassword}
                    onChange={(e) =>
                      setResetPasswordData((prev) => ({
                        ...prev,
                        newPassword: e.target.value,
                      }))
                    }
                    placeholder="Unesite novu šifru"
                    className={errors.newPassword ? "error" : ""}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={loading}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordInputWrapper>
                {errors.newPassword && (
                  <ErrorText>{errors.newPassword}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>
                  <FiLock style={{ marginRight: "0.5rem" }} />
                  Potvrdi Šifru *
                </FormLabel>
                <PasswordInputWrapper>
                  <FormInput
                    type={showConfirmPassword ? "text" : "password"}
                    value={resetPasswordData.confirmPassword}
                    onChange={(e) =>
                      setResetPasswordData((prev) => ({
                        ...prev,
                        confirmPassword: e.target.value,
                      }))
                    }
                    placeholder="Ponovite novu šifru"
                    className={`${errors.confirmPassword ? "error" : ""} ${
                      passwordsMatch ? "success" : ""
                    } ${passwordsDontMatch ? "mismatch" : ""}`}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordInputWrapper>
                {errors.confirmPassword && (
                  <ErrorText>{errors.confirmPassword}</ErrorText>
                )}
                {passwordsMatch && !errors.confirmPassword && (
                  <SuccessText>✓ Šifre se poklapaju</SuccessText>
                )}
                {passwordsDontMatch && !errors.confirmPassword && (
                  <ErrorText>Šifre se ne poklapaju</ErrorText>
                )}
              </FormGroup>

              <ButtonGroup>
                <Button type="submit" $variant="primary" disabled={loading}>
                  {loading ? "Resetujem..." : "Reset Password"}
                </Button>
                <Button
                  type="button"
                  $variant="secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  Otkaži
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default DashboardAccessModals;

// Stilovi
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid #ff6b35;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  color: #ff6b35;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: rotate(90deg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BandInfo = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid #ff6b35;

  p {
    margin: 0.25rem 0;
    color: #cccccc;
    font-size: 0.9rem;
  }

  strong {
    color: #ff6b35;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: flex;
  align-items: center;
  color: #ff6b35;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  padding-right: 3rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &.error {
    border-color: #e74c3c;
  }

  &.success {
    border-color: #27ae60;
  }

  &.mismatch {
    border-color: #e74c3c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.8rem;
  background: none;
  border: none;
  color: #ff6b35;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #e55a2b;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.8rem 1.2rem;
  min-height: 3rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;

  background: ${(props) => {
    switch (props.$variant) {
      case "primary":
        return "#ff6b35";
      case "secondary":
        return "#666";
      default:
        return "#ff6b35";
    }
  }};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${(props) => {
      switch (props.$variant) {
        case "primary":
          return "#e55a2b";
        case "secondary":
          return "#555";
        default:
          return "#e55a2b";
      }
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const SuccessText = styled.div`
  color: #27ae60;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;
